/**
 * Stroke text.
 *
 * CIRCAD draws every string with the pen, not with system glyphs, which is why
 * its silkscreen and schematics look the way they do — and why exported Gerber
 * text is real copper/silk geometry rather than a font reference. We use the
 * original's own font, extracted from cgtzz[].
 *
 * The layout rules follow the SGC structure in OG.h:469 — height, aspect ratio
 * (width/height), rotation, and a nine-point anchor.
 */

import { STROKE_FONT, type StrokeGlyph } from './stroke-font.ts';
import type { TextAnchor } from '../model.ts';

export interface TextLayout {
  /** Polylines in document space. */
  strokes: number[][];
  /** Untransformed extents, for hit-testing and bounds. */
  width: number;
  height: number;
}

const SPACE_ADVANCE = 0.833;

function glyph(code: number): StrokeGlyph | undefined {
  return STROKE_FONT[code];
}

/** Total advance of a string in cell units. */
export function measure(text: string): number {
  let w = 0;
  for (const ch of text) {
    const code = ch.charCodeAt(0);
    const g = glyph(code);
    w += g && g.w > 0 ? g.w : (code === 32 ? SPACE_ADVANCE : 0);
  }
  return w;
}

/**
 * Anchor offsets for the *first* line's cell.
 *
 * The insertion point is the anchor of the first line, and lines stack
 * *downward* from it (see {@link layoutText}); this matches CIRCAD, where a
 * bounded note grows down into its box rather than up into the text above it.
 * The vertical offset is therefore in cell units of a single line, not the
 * whole block: baseline at the insertion (B), one cell down for top (T), half
 * for middle (M). The cell origin is the baseline and the cell is one unit tall.
 */
function anchorOffset(anchor: TextAnchor | undefined, w: number): [number, number] {
  const a = anchor ?? 'BL';
  const horizontal = a.charAt(1);
  const vertical = a.charAt(0);
  const dx = horizontal === 'C' ? -w / 2 : horizontal === 'R' ? -w : 0;
  const dy = vertical === 'M' ? -0.5 : vertical === 'T' ? -1 : 0;
  return [dx, dy];
}

export interface StrokeTextOptions {
  x: number;
  y: number;
  /** Character cell height in document units. */
  height: number;
  /** Width/height ratio; 1 is natural. */
  aspect?: number;
  /** Degrees counter-clockwise. */
  rotation?: number;
  anchor?: TextAnchor;
  mirrored?: boolean;
}

/**
 * Lay a string out into document-space polylines.
 *
 * Multi-line strings split on newline and stack *downward* from the insertion
 * at 1.5× height, which matches the original's `Lines:` spacing: the first line
 * sits at the insertion and each further line drops below it, so a bounded note
 * fills its box downward rather than climbing up into the text above.
 */
export function layoutText(text: string, o: StrokeTextOptions): TextLayout {
  const aspect = o.aspect && o.aspect > 0 ? o.aspect : 1;
  // CIRCAD wraps a bounded string by inserting 0xFF at each line break (the
  // `Lines:` count in the file). Split on it — and on any real newline — so the
  // text stacks into its box instead of running off in one line.
  const lines = text.split(/[\r\nÿ]/);
  const lineHeight = 1.5;

  const widths = lines.map(measure);
  const blockWidth = Math.max(0, ...widths);
  const blockHeight = lines.length === 1 ? 1 : (lines.length - 1) * lineHeight + 1;

  const [ax, ay] = anchorOffset(o.anchor, blockWidth);
  const halign = (o.anchor ?? 'BL').charAt(1);

  const rad = ((o.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const mirror = o.mirrored ? -1 : 1;

  const strokes: number[][] = [];

  lines.forEach((line, li) => {
    // The first line sits at the insertion; each later line drops below it, so
    // the block grows downward into its box. Each line is aligned within the
    // block width so centred/right text tracks the box, not just the first line.
    const lineDx =
      halign === 'C' ? (blockWidth - widths[li]!) / 2
      : halign === 'R' ? blockWidth - widths[li]!
      : 0;
    let penX = ax + lineDx;
    const penY = ay - li * lineHeight;

    for (const ch of line) {
      const code = ch.charCodeAt(0);
      const g = glyph(code);
      if (!g) { penX += SPACE_ADVANCE; continue; }

      for (const s of g.s) {
        const out: number[] = new Array(s.length);
        for (let i = 0; i < s.length; i += 2) {
          // Cell units -> scaled, mirrored, rotated, translated document units.
          const cxu = (penX + s[i]!) * aspect * mirror;
          const cyu = penY + s[i + 1]!;
          const sx = cxu * o.height;
          const sy = cyu * o.height;
          out[i] = o.x + sx * cos - sy * sin;
          out[i + 1] = o.y + sx * sin + sy * cos;
        }
        strokes.push(out);
      }

      // Zero-advance glyphs (0xD0-0xDB) are overlay marks and must not move
      // the pen; a space has no strokes but does advance.
      penX += g.w > 0 ? g.w : (code === 32 ? SPACE_ADVANCE : 0);
    }
  });

  return {
    strokes,
    width: blockWidth * aspect * o.height,
    height: blockHeight * o.height,
  };
}

/**
 * Resolve the field markers the stock libraries use in component text.
 *
 * `&1`…`&9` are numbered parameters, e.g. a capacitor's `&1` becomes its
 * reference designator. `&@` is the name of the net attached to the part — the
 * port symbols (`$$SIG`, `$$PWR`) label themselves with it, so `&@` becomes
 * `KEYLOAD`, `Vcc`, and so on from the pin's signal. Unresolved markers render
 * as the raw template so a missing value is visible rather than silently blank.
 */
export function substituteParams(
  text: string, params: Record<number, string>, net?: string,
): string {
  let out = text.replace(/&([1-9])/g, (whole, digit: string) => {
    const v = params[parseInt(digit, 10)];
    return v === undefined || v === '' ? whole : v;
  });
  if (net) out = out.replace(/&@/g, net);
  return out;
}
